import { RouteComponentProps } from "react-router";
import { useEffect } from "react";
import { Box, CircularProgress, Container } from "@material-ui/core";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "swyft-react-core";

import { useStyles } from "./quoteDetail.styles";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { EEventType, eventAnalytic } from "../../app/analytics";
import { RootState } from "../../app/rootReducer";
import { fetchProductSubscriptions } from "../../store/ProductSubscriptionSlice";
import { fetchQuote, fetchSolarReport, syncQuote } from "../../store/QuoteSlice";
import { SaleBanner } from "../../components/SaleBanner";
import { CookieQuoteId } from "../../app/consts";
import { Header } from "./components/Header";
import { QuoteInfoSection } from "./components/QuoteInfoSection";
import { TopBox } from "../../components/TopBox";
import { BotBox } from "../../components/BotBox";
import { QuoteQuotationSection } from "./components/QuoteQuotationSection";
import { AssessmentCommentsSection } from "./components/AssessmentCommentsSection";
import { AddOnSection } from "./components/AddOnSection";
import { hasAddons, hasSubscriptions, quoteAvailableSubscriptions } from "../../app/utils";
import { EQuoteType, PageState } from "../../api/Types";
import { CustomerSay } from "./components/CustomerSay";
import { customerSliderByType } from "./quoteDetail.constants";
import { FinancialSupports } from "./components/FinancialSupports";
import { EnvironmentalImpactSection } from "./components/EnvironmentalImpactSection";
import { GraphsSection } from "./components/GraphsSection";
import { Title } from "./components/Title";
import { ElectricityAnalysisSection } from "./components/ElectricityAnalysisSection";
import { WhatHappensNextSection } from "./components/WhatHappensNextSection";
import { WhyChooseUsSection } from "./components/WhyChooseUsSection";
import { AfterCareSection } from "./components/AfterCareSection";
import { BookNowButton } from "./components/BookNowButton";
import { EQuoteStatus } from "../../api/Quote/EQuoteStatus";
import { shouldRedirectQuote, getCustomerQuoteRedirectUrl } from "../../app/redirects";

type Props = RouteComponentProps<{ id: string }>;
export const QuoteDetailPage = ({ match }: Props) => {
  eventAnalytic.useEffect(EEventType.PageQuoteView);

  const classes = useStyles();
  const { isMoreThanMd, isMoreThanSm } = useBreakpoint();
  const [cookies, setCookie] = useCookies();
  const dispatch = useDispatch();
  const { quote, pageState, solarReport } = useSelector((state: RootState) => state.quote);
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);

  const quoteId = match.params.id;

  useEffect(() => {
    dispatch(fetchProductSubscriptions());
    dispatch(fetchQuote(quoteId));
    dispatch(syncQuote(quoteId, true));
  }, [quoteId, dispatch]);

  useEffect(() => {
    if (quote?.type === EQuoteType.Solar) {
      dispatch(fetchSolarReport({ quoteId }));
    }
  }, [dispatch, quote, quoteId]);

  eventAnalytic.useQuoteEffect(quote);

  if (quote && cookies[CookieQuoteId] !== quote.id) {
    setCookie(CookieQuoteId, quote.id, { path: "/" });
  }

  const availableSubscriptions = quote && subscriptions && quoteAvailableSubscriptions(quote, subscriptions);

  const isGraphVisible =
    quote?.isGraphVisible && solarReport !== null && Number(solarReport.electricityEstimatedAnnualSpend) > 0;
  const isAddonSectionHidden = !hasAddons(quote?.addons) && !hasSubscriptions(availableSubscriptions);

  const showSpecialBackgroundConditions =
    (isGraphVisible && !isAddonSectionHidden) || (!isGraphVisible && isAddonSectionHidden);

  const showSpecialBackgroundConditionsEnvironmentShow =
    isGraphVisible && isAddonSectionHidden ? !showSpecialBackgroundConditions : showSpecialBackgroundConditions;

  const customerSliderValues = customerSliderByType(quote?.type);

  if (quote && shouldRedirectQuote(quote)) {
    window.location.href = getCustomerQuoteRedirectUrl(quote.id);
    return <Loader />;
  }

  return (
    <>
      <Box bgcolor={"#fff"} className={classes.root} position={"relative"}>
        {pageState === PageState.Loading && (
          <Box
            position={"fixed"}
            top={0}
            left={0}
            width={"100%"}
            height="100vh"
            bgcolor={"rgba(255,255,255,0.5)"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            zIndex={1302}
          >
            <CircularProgress color="inherit" />
          </Box>
        )}
        <SaleBanner quoteId={quoteId} />
        <Container maxWidth={"lg"}>
          <Header />
        </Container>
        {!quote ? (
          <Loader />
        ) : (
          <>
            <Title name={quote.customer?.firstName || quote.customer?.name || ""} quoteType={quote.type} />

            <QuoteInfoSection quote={quote} />

            <QuoteQuotationSection quote={quote} />

            {quote.isGraphVisible && (
              <GraphsSection solarReportData={solarReport} isSolar={quote?.type === EQuoteType.Solar} />
            )}

            {quote.customerText && <AssessmentCommentsSection quote={quote} isGraphVisible={isGraphVisible} />}

            <AddOnSection quote={quote} subscriptions={availableSubscriptions || []} isGraphVisible={isGraphVisible} />

            {quote.type === EQuoteType.Solar && (
              <ElectricityAnalysisSection hasSpecialBackground={showSpecialBackgroundConditions} />
            )}

            <WhatHappensNextSection
              hasSpecialBackground={quote.type === EQuoteType.Solar && !showSpecialBackgroundConditions}
            />

            {quote.type === EQuoteType.Solar && (
              <EnvironmentalImpactSection solarReportData={solarReport} isGraphVisible={isGraphVisible} />
            )}

            {quote.type === EQuoteType.Boiler && (
              <WhyChooseUsSection hasSpecialBackground={showSpecialBackgroundConditions} />
            )}

            {(quote.type === EQuoteType.Solar || quote.type === EQuoteType.Boiler) && (
              <Box
                bgcolor={
                  quote.type === EQuoteType.Solar && showSpecialBackgroundConditionsEnvironmentShow ? "#f7f7f7" : "#fff"
                }
                mt={"97px"}
                position={"relative"}
              >
                {quote.type === EQuoteType.Solar && showSpecialBackgroundConditionsEnvironmentShow && (
                  <TopBox bgColor="#f7f7f7" outsideBgColor="#fff" boxHeight={isMoreThanMd ? "145px" : "71px"} />
                )}
                <FinancialSupports
                  quoteType={quote.type}
                  purchaserType={quote.purchaserType}
                  hasSpecialBackground={showSpecialBackgroundConditionsEnvironmentShow}
                />
                {quote.type === EQuoteType.Solar && showSpecialBackgroundConditionsEnvironmentShow && (
                  <BotBox bgColor="#f7f7f7" outsideBgColor="#fff" boxHeight={isMoreThanMd ? "145px" : "71px"} />
                )}
              </Box>
            )}
            {quote.type === EQuoteType.Solar && (
              <>
                <WhyChooseUsSection hasSpecialBackground={!showSpecialBackgroundConditionsEnvironmentShow} />
                <AfterCareSection hasSpecialBackground={showSpecialBackgroundConditionsEnvironmentShow} />
              </>
            )}
            <Box mt={"105px"} mb={"124px"}>
              {customerSliderValues?.length && <CustomerSay customerSlider={customerSliderValues} />}
            </Box>
            {!isMoreThanSm && quote.status === EQuoteStatus.Sent && <BookNowButton quoteId={quote.id} />}
          </>
        )}
      </Box>
    </>
  );
};
